import { makeStyles } from "@material-ui/core";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { PL_FONT, PL_TH_BG, PL_TH_TEXT, PL_TH_BORDER, PL_POS_C, PL_NEG_C } from "./plByProgramTheme";
import { ROW_DEFS } from "./actualsVsForecastByDateRangeUtils";
import { applyNumFilter, isNumFilterActive, calcOpDropdownPos, renderStandardNumFilter, renderStandardTextFilter } from "./plTableFilterUtils";
import { OpDropdownContent } from "../waterfall-eac/tableColumnFilterShared";
import { calcFilterPos, FILTER_PANEL_WIDTH_NUMERIC } from "../waterfall-eac/filterPopoverUtils";
import { TH_ACTUAL_BG, TH_ACTUAL_TEXT } from "../../reportTableConfig";

const HAIRLINE = "#E2E8F0";
const HEADER_TOP = PL_TH_BG;
const HEADER_ROW = PL_TH_BG;
const HEADER_LABEL = PL_TH_TEXT;
const CELL_TEXT = "#374151";
const TEXT_MUTED = "#6B7280";
const ROW_STRIPE = "#F7FAFC";
const ACTUAL_TH_BG = TH_ACTUAL_BG;
const ACTUAL_TH_FG = TH_ACTUAL_TEXT;

const ROW_STYLE = {
  data: { labelColor: CELL_TEXT, fontWeight: 400 },
  subtotal: { labelColor: CELL_TEXT, fontWeight: 700 },
  metric: { labelColor: CELL_TEXT, fontWeight: 700 },
  pct: { labelColor: CELL_TEXT, fontWeight: 400 },
};

const fmtAbs = (v) => Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 0 });

function fmtCell(v, isPct) {
  if (isPct) return `${v.toFixed(1)}%`;
  if (v === 0) return "—";
  return v < 0 ? `($${fmtAbs(v)})` : `$${fmtAbs(v)}`;
}

function fmtVariance(v, isPct) {
  const n = Number(v);
  const val = Number.isFinite(n) ? n : 0;
  if (isPct) {
    if (val === 0) return "0.0%";
    return val > 0 ? `${val.toFixed(1)}%` : `(${Math.abs(val).toFixed(1)}%)`;
  }
  if (val === 0) return "$0";
  return val > 0 ? `$${fmtAbs(val)}` : `($${fmtAbs(val)})`;
}

const varColor = (v) => (v === 0 || v == null ? CELL_TEXT : v > 0 ? PL_POS_C : PL_NEG_C);

export const useDateRangeTableStyles = makeStyles(() => ({
  tableSection: {
    marginBottom: 24,
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    overflow: "visible",
  },
  tableSectionHead: {
    padding: "16px 20px 14px 24px",
    borderBottom: `1px solid ${HAIRLINE}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  tableSectionHeadText: { minWidth: 0 },
  tableSectionTitle: {
    fontSize: 15, fontWeight: 600, color: CELL_TEXT, letterSpacing: "-0.02em",
    lineHeight: 1.35, fontFamily: PL_FONT, margin: 0,
  },
  tableSectionMeta: {
    marginTop: 4, fontSize: 12, fontWeight: 400, color: TEXT_MUTED, fontFamily: PL_FONT, lineHeight: 1.4,
  },
  tableWrap: {
    overflowX: "auto", overflowY: "hidden", background: "#FFFFFF",
    borderBottomLeftRadius: 12, borderBottomRightRadius: 12,
  },
  table: { width: "100%", borderCollapse: "collapse", fontFamily: PL_FONT, fontSize: 13, tableLayout: "auto" },
  thead: { position: "sticky", top: 0, zIndex: 2 },
  thMetric: {
    background: HEADER_TOP, color: HEADER_LABEL, padding: "14px 20px", fontWeight: 600, fontSize: 10,
    textAlign: "left", whiteSpace: "nowrap", minWidth: 180, position: "sticky", left: 0, zIndex: 4,
    letterSpacing: "0.08em", textTransform: "uppercase", verticalAlign: "middle",
    borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thBandActuals: {
    background: HEADER_TOP, color: HEADER_LABEL, padding: "12px 10px", fontWeight: 600, fontSize: 10,
    textAlign: "center", whiteSpace: "nowrap", letterSpacing: "0.06em", textTransform: "uppercase",
    borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thBandForecast: {
    background: HEADER_TOP, color: HEADER_LABEL, padding: "12px 10px", fontWeight: 600, fontSize: 10,
    textAlign: "center", whiteSpace: "nowrap", letterSpacing: "0.06em", textTransform: "uppercase",
    borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thBandTotal: {
    background: HEADER_TOP, color: HEADER_LABEL, padding: "12px 10px", fontWeight: 600, fontSize: 10,
    textAlign: "center", whiteSpace: "nowrap", letterSpacing: "0.06em", textTransform: "uppercase",
    minWidth: 88, verticalAlign: "middle",
    borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thMonthActual: {
    background: ACTUAL_TH_BG, color: ACTUAL_TH_FG, padding: "12px 10px", fontWeight: 500, fontSize: 12,
    textAlign: "right", minWidth: 84, whiteSpace: "nowrap", borderRight: `1px solid ${PL_TH_BORDER}`,
    "& $filterIconBtn": { color: ACTUAL_TH_FG, opacity: 0.65, "&:hover": { opacity: 1, background: "rgba(0,0,0,0.08)" } },
    "& $filterIconBtnActive": { opacity: 1, color: ACTUAL_TH_FG, background: "rgba(0,0,0,0.12)" },
  },
  thMonthForecast: {
    background: HEADER_ROW, color: HEADER_LABEL, padding: "12px 10px", fontWeight: 500, fontSize: 12,
    textAlign: "right", minWidth: 84, whiteSpace: "nowrap", borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thColTotal: {
    background: HEADER_ROW, color: HEADER_LABEL, padding: "12px 10px", fontWeight: 600, fontSize: 12,
    textAlign: "right", minWidth: 88, whiteSpace: "nowrap", borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  trRow: { transition: "background-color 0.12s ease" },
  trStripe: { "& td": { background: ROW_STRIPE }, "& td:first-child": { background: ROW_STRIPE } },
  tdLabel: {
    padding: "13px 20px", whiteSpace: "nowrap", borderBottom: `1px solid ${HAIRLINE}`, fontSize: 13,
    position: "sticky", left: 0, zIndex: 1, background: "#FFFFFF",
    boxShadow: "6px 0 12px -8px rgba(0,0,0,0.06)", textTransform: "capitalize",
  },
  tdActual: {
    padding: "13px 14px", textAlign: "right", color: CELL_TEXT, borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 13, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", background: "#FFFFFF",
  },
  tdForecast: {
    padding: "13px 14px", textAlign: "right", color: CELL_TEXT, borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 13, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", background: "#FFFFFF",
  },
  tdTotal: {
    padding: "13px 14px", textAlign: "right", color: CELL_TEXT, borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", background: "#FFFFFF",
  },
  tdVariance: {
    padding: "13px 14px", textAlign: "right", color: CELL_TEXT, borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 13, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", background: "#FFFFFF",
  },
  filterLabelCell: { padding: "5px 10px", position: "sticky", left: 0, zIndex: 3, background: "#F1F5F9" },
  filterIconBtn: {
    flexShrink: 0, width: 16, height: 16, border: "none", background: "transparent", cursor: "pointer",
    padding: 0, borderRadius: 3, color: PL_TH_TEXT, opacity: 0.5, display: "flex", alignItems: "center",
    justifyContent: "center", "&:hover": { opacity: 1, background: "rgba(255,255,255,0.14)" },
  },
  filterIconBtnActive: { opacity: 1, color: "#A5B4FC", background: "rgba(165,180,252,0.18)", borderRadius: 3 },
  filterPanel: {
    position: "fixed", zIndex: 99, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: "10px 12px", minWidth: 180, fontFamily: PL_FONT,
  },
}));

function FilterFunnelIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

export default function ActualsVsForecastByDateRangeTable({ data, isVariance, cols, actualCols, wrapRef, onScroll, title, meta }) {
  const classes = useDateRangeTableStyles();
  const [labelFilter, setLabelFilter] = useState("");
  const [colFilters, setColFilters] = useState({});
  const [openOpKey, setOpenOpKey] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const opBtnRefs = useRef({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});

  const monthCols = cols.filter((c) => !c.isTotal);
  const bandActualCount = monthCols.filter((c) => actualCols.has(c.key)).length;
  const bandForecastCount = monthCols.length - bandActualCount;

  const updateColFilter = useCallback((colKey, field, value) =>
    setColFilters((prev) => ({ ...prev, [colKey]: { op: "gt", ...prev[colKey], [field]: value } })), []);
  const clearColFilter = useCallback((colKey) =>
    setColFilters((prev) => { const n = { ...prev }; delete n[colKey]; return n; }), []);
  const isColFilterActive = (colKey) => isNumFilterActive(colFilters[colKey]);

  const toggleOpDropdown = (colKey) => {
    if (openOpKey === colKey) { setOpenOpKey(null); return; }
    const btn = opBtnRefs.current[colKey];
    if (btn) setDropdownPos(calcOpDropdownPos(btn));
    setOpenOpKey(colKey);
  };
  const selectOp = (colKey, opKey) => { updateColFilter(colKey, "op", opKey); setOpenOpKey(null); };

  useEffect(() => {
    if (!openOpKey) return;
    const handler = (e) => {
      if (!e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]") && !e.target.closest("[data-op-btn]"))
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openOpKey]);

  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (!e.target.closest("[data-filter-panel]") && !e.target.closest("[data-filter-btn]") && !e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]"))
        setOpenFilterKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  const rowPasses = (row) => {
    if (labelFilter && !row.label.toLowerCase().includes(labelFilter.toLowerCase())) return false;
    for (const [colKey, filter] of Object.entries(colFilters)) {
      if (!isColFilterActive(colKey)) continue;
      if (!applyNumFilter(data[row.key]?.[colKey] ?? 0, filter)) return false;
    }
    return true;
  };

  const cellClass = (col) => {
    if (col.isTotal) return classes.tdTotal;
    if (isVariance) return classes.tdVariance;
    return actualCols.has(col.key) ? classes.tdActual : classes.tdForecast;
  };
  const rowClass = (idx) => (idx % 2 === 1 ? classes.trStripe : "");
  const cellWeight = (row) => (row.type === "subtotal" || row.type === "metric" ? 700 : ROW_STYLE[row.type].fontWeight);
  const getColLabel = (colKey) => cols.find((c) => c.key === colKey)?.label || colKey;

  const renderNumFilterDiv = (colKey) => renderStandardNumFilter({
    label: getColLabel(colKey), colKey, colFilters, updateColFilter, clearColFilter, toggleOpDropdown, opBtnRefs,
  });
  const hasFilterValue = (key) => (key === "label" ? !!labelFilter : isColFilterActive(key));
  const renderFilterContent = (key) =>
    key === "label"
      ? renderStandardTextFilter({ label: "Metric", value: labelFilter, onChange: setLabelFilter, onClear: () => setLabelFilter(""), placeholder: "Search metrics…" })
      : renderNumFilterDiv(key);

  const toggleFilter = (key) => {
    if (openFilterKey === key) { setOpenFilterKey(null); return; }
    const btn = filterBtnRefs.current[key];
    if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  };

  return (
    <div className={classes.tableSection}>
      <div className={classes.tableSectionHead}>
        <div className={classes.tableSectionHeadText}>
          <h2 className={classes.tableSectionTitle}>{title}</h2>
          {meta && <div className={classes.tableSectionMeta}>{meta}</div>}
        </div>
      </div>

      <div className={classes.tableWrap} ref={wrapRef} onScroll={onScroll}>
        <table className={classes.table}>
          <thead className={classes.thead}>
            <tr>
              <th className={classes.thMetric} rowSpan={2}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                  <span style={{ flex: 1, color: openFilterKey === "label" ? "#60A5FA" : undefined }}>Metric</span>
                  <button ref={(el) => { filterBtnRefs.current["label"] = el; }} data-filter-btn
                    className={`${classes.filterIconBtn} ${hasFilterValue("label") ? classes.filterIconBtnActive : ""}`}
                    onClick={() => toggleFilter("label")} title="Filter">
                    <FilterFunnelIcon />
                  </button>
                </div>
              </th>
              {isVariance ? (
                (bandActualCount + bandForecastCount) > 0 &&
                <th className={classes.thBandActuals} colSpan={bandActualCount + bandForecastCount}>Range</th>
              ) : (
                <>
                  {bandActualCount > 0 && <th className={classes.thBandActuals} colSpan={bandActualCount}>Actuals</th>}
                  {bandForecastCount > 0 && <th className={classes.thBandForecast} colSpan={bandForecastCount}>Forecast</th>}
                </>
              )}
              <th className={classes.thBandTotal}>Total</th>
            </tr>
            <tr>
              {monthCols.map((c) => (
                <th key={c.key} className={actualCols.has(c.key) ? classes.thMonthActual : classes.thMonthForecast}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                    <span style={{ color: actualCols.has(c.key) ? ACTUAL_TH_FG : openFilterKey === c.key ? "#60A5FA" : undefined }}>{c.label}</span>
                    <button ref={(el) => { filterBtnRefs.current[c.key] = el; }} data-filter-btn
                      className={`${classes.filterIconBtn} ${hasFilterValue(c.key) ? classes.filterIconBtnActive : ""}`}
                      onClick={() => toggleFilter(c.key)} title="Filter">
                      <FilterFunnelIcon />
                    </button>
                  </div>
                </th>
              ))}
              <th className={classes.thColTotal}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                  <span style={{ color: openFilterKey === "total" ? "#60A5FA" : undefined }}>Total</span>
                  <button ref={(el) => { filterBtnRefs.current["total"] = el; }} data-filter-btn
                    className={`${classes.filterIconBtn} ${hasFilterValue("total") ? classes.filterIconBtnActive : ""}`}
                    onClick={() => toggleFilter("total")} title="Filter">
                    <FilterFunnelIcon />
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {ROW_DEFS.map((row, idx) => {
              const rs = ROW_STYLE[row.type];
              const visible = rowPasses(row);
              return (
                <tr key={row.key} className={`${classes.trRow} ${rowClass(idx)}`} style={visible ? {} : { display: "none" }}>
                  <td className={classes.tdLabel} style={{ color: rs.labelColor, fontWeight: rs.fontWeight }}>{row.label}</td>
                  {cols.map((col) => {
                    const rawVal = data?.[row.key]?.[col.key] ?? 0;
                    const text = isVariance ? fmtVariance(rawVal, row.isPct) : fmtCell(rawVal, row.isPct);
                    return (
                      <td key={col.key} className={cellClass(col)}
                        style={{ fontWeight: cellWeight(row), ...(isVariance ? { color: varColor(rawVal) } : {}) }}>
                        {text}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        {openOpKey && createPortal(
          <OpDropdownContent
            activeOp={(colFilters[openOpKey]?.op) || "gt"}
            onSelect={(op) => selectOp(openOpKey, op)}
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          />,
          document.body
        )}
        {openFilterKey && createPortal(
          <div data-filter-panel className={classes.filterPanel} style={{ top: filterPanelPos.top, left: filterPanelPos.left }}>
            {renderFilterContent(openFilterKey)}
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}
